<template>
  <div class="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
    <div class="w-full max-w-md">
      <div id="register-card" class="w-full">
          <!-- Logo -->
          <div class="flex justify-center mb-6">
            <div class="h-16 w-16 bg-primary-600 dark:bg-primary-500 rounded-xl flex items-center justify-center shadow-sm">
              <svg class="h-9 w-9 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V6C15 6.55228 14.5523 7 14 7H10C9.44772 7 9 6.55228 9 6V4Z"/>
                <rect x="10" y="7" width="4" height="8" rx="0.5" fill="currentColor"/>
                <circle cx="12" cy="17" r="1" fill="currentColor"/>
                <path d="M11 10L11.5 10.5L13 9" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Title -->
          <div class="text-center mb-8">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {{ t('auth.register.title') }}
            </h1>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              Or <router-link to="/login" class="text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300">{{ t('auth.register.signInLink') }}</router-link>
            </p>
          </div>

          <!-- Register Form -->
          <HLForm :model="form" @submit.prevent="handleRegister">
            <HLSpace vertical :size="16">
              <!-- Name -->
                <HLFormItem 
                  :label="t('auth.register.fullNameLabel')" 
                  path="name" 
                  :validation-status="errors.name ? 'error' : undefined" 
                  required
                >
                  <HLInput
                    id="name"
                    v-model:modelValue="form.name"
                    type="text"
                    :placeholder="t('auth.register.fullNamePlaceholder')"
                    size="lg"
                    :disabled="loading"
                  />
                  <template v-if="errors.name" #feedback>{{ errors.name }}</template>
                </HLFormItem>

                <!-- Email -->
                <HLFormItem 
                  :label="t('auth.register.emailLabel')" 
                  path="email" 
                  :validation-status="errors.email ? 'error' : undefined" 
                  required
                >
                  <HLInput
                    id="email"
                    v-model:modelValue="form.email"
                    type="email"
                    :placeholder="t('auth.register.emailPlaceholder')"
                    size="lg"
                    autocomplete="email"
                    :disabled="loading"
                  />
                  <template v-if="errors.email" #feedback>{{ errors.email }}</template>
                  <template v-else #extra>
                    <span class="text-xs text-gray-500">{{ t('auth.register.emailHint') }}</span>
                  </template>
                </HLFormItem>

                <!-- Password -->
                <HLFormItem 
                  :label="t('auth.register.passwordLabel')" 
                  path="password" 
                  :validation-status="errors.password ? 'error' : undefined" 
                  required
                >
                  <HLInput
                    id="password"
                    v-model:modelValue="form.password"
                    type="password"
                    :placeholder="t('auth.register.passwordPlaceholder')"
                    size="lg"
                    autocomplete="new-password"
                    :disabled="loading"
                  />
                  <template v-if="errors.password" #feedback>{{ errors.password }}</template>
                </HLFormItem>

                <!-- Confirm Password -->
                <HLFormItem 
                  :label="t('auth.register.confirmPasswordLabel')" 
                  path="confirmPassword" 
                  :validation-status="errors.confirmPassword ? 'error' : undefined" 
                  required
                >
                  <HLInput
                    id="confirmPassword"
                    v-model:modelValue="form.confirmPassword"
                    type="password"
                    :placeholder="t('auth.register.confirmPasswordPlaceholder')"
                    size="lg"
                    autocomplete="new-password"
                    :disabled="loading"
                  />
                  <template v-if="errors.confirmPassword" #feedback>{{ errors.confirmPassword }}</template>
                </HLFormItem>

                <!-- Role -->
                <HLFormItem 
                  :label="t('auth.register.roleLabel')" 
                  path="role" 
                  required
                >
                  <HLSelect
                    id="role"
                    v-model:value="form.role"
                    size="lg"
                    :disabled="loading"
                    :options="roleOptions"
                  />
                </HLFormItem>

              <!-- Terms and Conditions -->
              <HLFormItem 
                path="agreeToTerms" 
                :validation-status="errors.agreeToTerms ? 'error' : undefined"
              >
                <HLCheckbox
                  id="agree-to-terms"
                  v-model:checked="form.agreeToTerms"
                >
                  <span class="text-sm text-gray-700 dark:text-gray-300">
                    I agree to the 
                    <a href="#" class="text-primary-600 hover:text-primary-500">Terms and Conditions</a>
                    and
                    <a href="#" class="text-primary-600 hover:text-primary-500">Privacy Policy</a>
                  </span>
                </HLCheckbox>
                <template v-if="errors.agreeToTerms" #feedback>{{ errors.agreeToTerms }}</template>
              </HLFormItem>

              <HLButton
                id="register-submit-btn"
                color="blue"
                variant="primary"
                size="lg"
                :disabled="loading"
                :loading="loading"
                block
                @click="handleRegister"
              >
                {{ loading ? t('auth.register.creatingAccount') : t('auth.register.createAccount') }}
              </HLButton>
            </HLSpace>
          </HLForm>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'
import { isValidEmailDomain, getInvalidDomainMessage } from '@/config/app.config'
import { USER_ROLE_EDIT_OPTIONS } from '@/constants/users'
import { 
  HLButton, 
  HLCheckbox, 
  HLInput, 
  HLForm, 
  HLFormItem, 
  HLSpace,
  HLSelect
} from '@platform-ui/highrise'
import { t } from '@/i18n/en-us'

// Router and stores
const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// Reactive data
const loading = ref(false)
const form = reactive({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'tester',
  agreeToTerms: false
})

const errors = reactive({
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  agreeToTerms: ''
})

// Role options for HLSelect (using computed to map to label format)
const roleOptions = computed(() =>
  USER_ROLE_EDIT_OPTIONS.map(option => ({
    value: option.value,
    label: t(option.labelKey)
  }))
)

// Lifecycle
onMounted(() => {
  console.log('Role Options:', roleOptions.value);
})

// Methods
const validateForm = () => {
  // Reset errors
  Object.keys(errors).forEach(key => {
    errors[key] = ''
  })

  let isValid = true

  // Name validation
  if (!form.name.trim()) {
    errors.name = 'Name is required'
    isValid = false
  } else if (form.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters'
    isValid = false
  }

  // Email validation
  if (!form.email.trim()) {
    errors.email = 'Email is required'
    isValid = false
  } else if (!form.email.includes('@') || !form.email.includes('.')) {
    errors.email = 'Please enter a valid email address'
    isValid = false
  } else if (!isValidEmailDomain(form.email)) {
    errors.email = getInvalidDomainMessage()
    isValid = false
  }

  // Password validation
  if (!form.password) {
    errors.password = 'Password is required'
    isValid = false
  } else if (form.password.length < 6) {
    errors.password = 'Password must be at least 6 characters'
    isValid = false
  }

  // Confirm password validation
  if (!form.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
    isValid = false
  } else if (form.password !== form.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
    isValid = false
  }

  // Terms and Conditions validation
  if (!form.agreeToTerms) {
    errors.agreeToTerms = 'You must agree to the Terms and Conditions'
    isValid = false
  }

  return isValid
}

const handleRegister = async () => {
  if (!validateForm()) {
    return
  }

  loading.value = true

  try {
    await authStore.signUp(
      form.email.trim(),
      form.password,
      {
        name: form.name.trim(),
        role: form.role
      },
      true // Default to remember me for new registrations
    )
    
    toast.success('Account created successfully!')
    router.push('/dashboard')
  } catch (error) {
    console.error('Registration error:', error)
    
    let errorMessage = 'Registration failed. Please try again.'
    
    // Handle API error codes
    if (error.code === 'EMAIL_ALREADY_EXISTS') {
      errorMessage = 'An account with this email already exists.'
    } else if (error.code === 'WEAK_PASSWORD') {
      errorMessage = 'Password must be at least 6 characters.'
    } else if (error.code === 'INVALID_EMAIL') {
      errorMessage = 'Please enter a valid email address.'
    } else if (error.code === 'MISSING_FIELDS') {
      errorMessage = 'Please fill in all required fields.'
    } else if (error.code === 'REGISTRATION_ERROR') {
      errorMessage = 'Registration failed. Please try again.'
    } else if (error.message) {
      errorMessage = error.message
    }

    toast.error(errorMessage)
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  // If already authenticated, redirect to dashboard
  if (authStore.isAuthenticated) {
    router.push('/dashboard')
  }
})
</script>